import {
  ActionIcon,
  Box,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
  createStyles,
} from '@mantine/core';
import React, {useRef, useState} from 'react';

import ModelHubModelIdentity from 'model-hub/lib/ModelHubModelIdentity';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ModelCapabilityLabels from './ModelCapabilityLabels';
import {getModelStructuralIncompatibility, type PredictionInput} from './newPredictionState';
import UseMsaCheckbox from './UseMsaCheckbox';

interface ModelSectionProps {
  availableModels: ReadonlyArray<ScientificModelNode>;
  // Current inputs, used to disable models whose shape can't run the current inputs (structural only).
  inputs: Array<PredictionInput>;
  // Per-model "Use MSAs" state, keyed by externalReference. MSA resolves per-model at submit.
  msaEnabledByModel: Readonly<Record<string, boolean>>;
  onAddModel: (model: ScientificModelNode) => void;
  onMSAEnabledChange: (externalReference: string, enabled: boolean) => void;
  onRemoveModel: (model: ScientificModelNode) => void;
  selectedModels: ReadonlyArray<ScientificModelNode>;
}

const MODEL_CARD_MIN_WIDTH_PX = 336;

export default function ModelSection({
  availableModels,
  inputs,
  msaEnabledByModel,
  onAddModel,
  onMSAEnabledChange,
  onRemoveModel,
  selectedModels,
}: ModelSectionProps): JSX.Element {
  const {classes, cx} = useStyles();
  const addModelCardRef = useRef<HTMLButtonElement>(null);
  const [activeMenu, setActiveMenu] = useState<ModelMenuAnchor | null>(null);
  const [cardMenuAnchor, setCardMenuAnchor] = useState<{x: number; y: number}>({x: 0, y: 0});
  const selectedReferences = new Set(selectedModels.map((model) => model.externalReference));
  const addableModels = availableModels.filter((model) => !selectedReferences.has(model.externalReference));
  const hasSelection = selectedModels.length > 0;

  const handleMenuChange = (anchor: ModelMenuAnchor) => (opened: boolean) => {
    setActiveMenu(opened ? anchor : null);
  };

  const openCardMenuAtPoint = (x: number, y: number): void => {
    setCardMenuAnchor({x, y});
    setActiveMenu('card');
  };

  const handleAddModelCardClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    openCardMenuAtPoint(event.clientX, event.clientY);
  };

  const handleAddModelCardKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    const rect = addModelCardRef.current?.getBoundingClientRect();
    if (rect == null) {
      return;
    }
    openCardMenuAtPoint(rect.left + rect.width / 2, rect.bottom);
  };

  const handleSelectModel = (model: ScientificModelNode): void => {
    setActiveMenu(null);
    onAddModel(model);
  };

  return (
    <Paper aria-label="Model settings" p="lg" radius="md" role="group">
      <Stack spacing="md">
        <Group align="center" noWrap={true} spacing={4}>
          <Title mb={0} order={2}>
            {hasSelection ? 'Models' : 'Model'}
          </Title>
          <Menu
            onChange={handleMenuChange('header')}
            opened={activeMenu === 'header'}
            position="bottom-start"
            withinPortal={true}
          >
            <Menu.Target>
              <ActionIcon aria-label="Add model" size="md" variant="subtle">
                <BenchlingIcon icon="plus" />
              </ActionIcon>
            </Menu.Target>
            <ModelPickerDropdown
              addableModels={addableModels}
              incompatibleHintClassName={classes.incompatibleHint}
              inputs={inputs}
              onSelectModel={handleSelectModel}
            />
          </Menu>
        </Group>
        {/* The dashed chooser card and the header "+" share one picker. This menu is positioned at the
            click point, so it needs its own invisible fixed target rather than the card itself. */}
        <Menu
          onChange={handleMenuChange('card')}
          opened={activeMenu === 'card'}
          position="bottom-start"
          positionDependencies={[cardMenuAnchor.x, cardMenuAnchor.y]}
          withinPortal={true}
        >
          <Menu.Target>
            <div
              aria-hidden={true}
              className={classes.menuPositionAnchor}
              style={{left: cardMenuAnchor.x, top: cardMenuAnchor.y}}
            />
          </Menu.Target>
          <ModelPickerDropdown
            addableModels={addableModels}
            incompatibleHintClassName={classes.incompatibleHint}
            inputs={inputs}
            onSelectModel={handleSelectModel}
          />
        </Menu>
        <div className={classes.modelCardGrid}>
          {selectedModels.map((model) => (
            <SelectedModelCard
              key={model.externalReference}
              model={model}
              msaEnabled={msaEnabledByModel[model.externalReference] ?? true}
              onMSAEnabledChange={onMSAEnabledChange}
              onRemoveModel={onRemoveModel}
            />
          ))}
          {/* Stays in the grid as the trailing "add another" tile once models are selected; the full
              explanatory copy only earns its space in the empty state. */}
          <UnstyledButton
            ref={addModelCardRef}
            aria-expanded={activeMenu === 'card'}
            aria-haspopup="menu"
            aria-label="Choose a model"
            className={cx(classes.addModelCard, {[classes.addModelCardFullWidth]: !hasSelection})}
            onClick={handleAddModelCardClick}
            onKeyDown={handleAddModelCardKeyDown}
            type="button"
          >
            <Stack align="center" spacing="md">
              <div className={classes.addModelIconTile}>
                <BenchlingIcon className={classes.addModelIcon} icon="hexagon-nodes" />
              </div>
              <Text className={classes.addModelTitle} size="md">
                {hasSelection ? 'Add another model' : 'Add a model'}
              </Text>
              {hasSelection ? null : (
                <Text color="dimmed" size="sm" ta="center">
                  Each model you add runs as its own batch.
                </Text>
              )}
            </Stack>
          </UnstyledButton>
        </div>
      </Stack>
    </Paper>
  );
}

interface SelectedModelCardProps {
  model: ScientificModelNode;
  msaEnabled: boolean;
  onMSAEnabledChange: (externalReference: string, enabled: boolean) => void;
  onRemoveModel: (model: ScientificModelNode) => void;
}

function SelectedModelCard({
  model,
  msaEnabled,
  onMSAEnabledChange,
  onRemoveModel,
}: SelectedModelCardProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();

  const handleRemoveModel = (): void => {
    onRemoveModel(model);
  };

  const handleMSAEnabledChange = (enabled: boolean): void => {
    onMSAEnabledChange(model.externalReference, enabled);
  };

  return (
    <Paper
      aria-label={model.displayName}
      className={classes.modelCard}
      p="md"
      radius="md"
      role="group"
      withBorder={true}
    >
      <Stack className={classes.modelCardStack} spacing="md">
        <Group align="flex-start" noWrap={true} position="apart">
          <ModelHubModelIdentity
            className={classes.modelIdentity}
            labels={<ModelCapabilityLabels model={model} />}
            model={model}
            showDescription={false}
          />
          <ActionIcon aria-label={`Remove ${model.displayName}`} onClick={handleRemoveModel} variant="subtle">
            <BenchlingIcon icon="trash-can-outline" />
          </ActionIcon>
        </Group>
        <Box mt="auto">
          <UseMsaCheckbox model={model} msaEnabled={msaEnabled} onMSAEnabledChange={handleMSAEnabledChange} />
        </Box>
      </Stack>
    </Paper>
  );
}

type ModelMenuAnchor = 'card' | 'header';

interface ModelPickerDropdownProps {
  addableModels: ReadonlyArray<ScientificModelNode>;
  incompatibleHintClassName: string;
  inputs: Array<PredictionInput>;
  onSelectModel: (model: ScientificModelNode) => void;
}

function ModelPickerDropdown({
  addableModels,
  incompatibleHintClassName,
  inputs,
  onSelectModel,
}: ModelPickerDropdownProps): JSX.Element {
  return (
    <Menu.Dropdown>
      {addableModels.map((model) => {
        // Structural-only: a model is disabled when the current inputs' shape can't run on it
        // (multimer input on a monomer model, or a ligand on a molecule-unsupported model).
        // Magnitude problems (AA length, input count) do NOT disable it — they flag per-row.
        const incompatibilityReason = getModelStructuralIncompatibility(model, inputs);
        const item = (
          <Menu.Item
            key={model.externalReference}
            disabled={incompatibilityReason != null}
            onClick={() => {
              onSelectModel(model);
            }}
            rightSection={
              incompatibilityReason != null ? (
                <BenchlingIcon className={incompatibleHintClassName} icon="info-circle" />
              ) : undefined
            }
          >
            {model.displayName}
          </Menu.Item>
        );
        // Wrap incompatible items in a Tooltip explaining why they can't be added. The tooltip
        // targets the info icon (an enabled child), since the disabled item itself takes no
        // pointer events.
        return incompatibilityReason == null ? (
          item
        ) : (
          <Tooltip key={model.externalReference} label={incompatibilityReason} withinPortal={true}>
            <div>{item}</div>
          </Tooltip>
        );
      })}
    </Menu.Dropdown>
  );
}

const useStyles = createStyles((theme) => ({
  addModelCard: {
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
      borderColor: theme.colors.gray[5],
    },
    border: `2px dashed ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.md,
    display: 'block',
    padding: `${theme.spacing.xl} ${theme.spacing.lg}`,
    transition: 'background-color 150ms ease, border-color 150ms ease',
    width: '100%',
  },
  // With no models selected the card is the whole section, so let it span every grid track instead of
  // sitting in a lone `minmax(336px, 1fr)` column.
  addModelCardFullWidth: {
    gridColumn: '1 / -1',
  },
  addModelIconTile: {
    alignItems: 'center',
    backgroundColor: theme.colors.gray[1],
    borderRadius: theme.radius.lg,
    display: 'flex',
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  addModelIcon: {
    color: theme.colors.gray[6],
    fontSize: 22,
  },
  addModelTitle: {
    fontWeight: 600,
  },
  menuPositionAnchor: {
    height: 0,
    pointerEvents: 'none',
    position: 'fixed',
    width: 0,
  },
  modelCardGrid: {
    display: 'grid',
    gap: theme.spacing.lg,
    gridTemplateColumns: `repeat(auto-fill, minmax(${MODEL_CARD_MIN_WIDTH_PX}px, 1fr))`,
  },
  modelCard: {
    borderColor: theme.colors.gray[1],
    height: '100%',
  },
  modelCardStack: {
    height: '100%',
  },
  modelIdentity: {
    flex: 1,
  },
  incompatibleHint: {
    color: theme.colors.gray[5],
  },
}));
